import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import moment from 'moment';
import PageNavbar from './PageNavbar';
import OrderSearch from './OrderSearch';
import { formatDateDisplay } from '../utils/formatDate';
import './AccountantView.css';

const PAYMENT_MODES = ['Online', 'Cash', 'Card', 'Cheque', 'Store-1-QR', 'Store-2-QR'];
const SETTLEMENT_TYPES = ['Full', 'Write-off', 'Credit'];

const CATEGORIES = ['Online Orders', 'Events', 'N Caterers', 'Store-1', 'Store-2', 'Desserts', 'Catering'];

const AccountantView = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [settlementFilter, setSettlementFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('date');
  const [sortAsc, setSortAsc] = useState(true);
  const [columnFilters, setColumnFilters] = useState({
    order_number: '',
    category: '',
    product: '',
    customer: '',
    agent: '',
    settlement: ''
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Add payment modal
  const [addPaymentModal, setAddPaymentModal] = useState(null);
  const [addPaymentForm, setAddPaymentForm] = useState({ amount: '', payment_mode: 'Cash', payment_date: moment().format('YYYY-MM-DD'), notes: '' });
  const [addPaymentSubmitting, setAddPaymentSubmitting] = useState(false);

  // Settle modal
  const [settleModal, setSettleModal] = useState(null);
  const [settleForm, setSettleForm] = useState({ settlement_type: 'Full', settlement_notes: '' });
  const [settleSubmitting, setSettleSubmitting] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { startDate, endDate };
      if (settlementFilter) params.settlementStatus = settlementFilter;
      const response = await api.get('/orders', { params });

      const ordersData = response.data?.orders ?? [];
      setOrders(Array.isArray(ordersData) ? ordersData : []);

      const totalRevenue = ordersData.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);
      const totalOutstanding = ordersData.reduce((sum, o) => sum + parseFloat(o.due_amount || 0), 0);
      const settledOrders = ordersData.filter(o => (o.settlement_status || 'Pending') === 'Settled');
      setSummary({
        totalRevenue,
        totalOrders: ordersData.length,
        totalOutstanding,
        totalSettled: settledOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0),
        settledCount: settledOrders.length
      });
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
      setOrders([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, settlementFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const openAddPayment = (order, e) => {
    e.stopPropagation();
    if ((order.settlement_status || 'Pending') === 'Settled') return;
    setAddPaymentModal(order);
    setAddPaymentForm({
      amount: String(order.due_amount || 0),
      payment_mode: 'Cash',
      payment_date: moment().format('YYYY-MM-DD'),
      notes: ''
    });
  };

  const closeAddPayment = () => {
    setAddPaymentModal(null);
    setAddPaymentSubmitting(false);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!addPaymentModal) return;
    setAddPaymentSubmitting(true);
    try {
      await api.post(`/orders/${addPaymentModal.id}/payments`, {
        amount: parseFloat(addPaymentForm.amount),
        payment_mode: addPaymentForm.payment_mode,
        payment_date: addPaymentForm.payment_date,
        notes: addPaymentForm.notes || undefined
      });
      closeAddPayment();
      fetchOrders();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add payment');
    } finally {
      setAddPaymentSubmitting(false);
    }
  };

  const openSettle = (order, e) => {
    e.stopPropagation();
    if ((order.settlement_status || 'Pending') === 'Settled') return;
    setSettleModal(order);
    setSettleForm({ settlement_type: 'Full', settlement_notes: '' });
  };

  const closeSettle = () => {
    setSettleModal(null);
    setSettleSubmitting(false);
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    if (!settleModal) return;
    const stype = settleForm.settlement_type;
    if ((stype === 'Write-off' || stype === 'Credit') && !settleForm.settlement_notes.trim()) {
      alert('Notes are required for Write-off or Credit');
      return;
    }
    setSettleSubmitting(true);
    try {
      await api.post(`/orders/${settleModal.id}/settle`, {
        settlement_type: stype,
        settlement_notes: settleForm.settlement_notes.trim() || undefined
      });
      closeSettle();
      fetchOrders();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to settle order');
    } finally {
      setSettleSubmitting(false);
    }
  };

  const getProductDescription = (order) => {
    const items = order.items || [];
    if (items.length === 0) return '-';
    const descs = items.map(item => {
      if (item.item_type === 'Cakes' && item.product_name) {
        return item.flavour ? `${item.product_name} (${item.flavour})` : item.product_name;
      }
      if (item.item_type === 'Desserts' && item.dessert_name) return item.dessert_name;
      if (item.item_type === 'Cup Cakes' && item.cup_cake_name) return item.cup_cake_name;
      if (item.item_type === 'Plants' && item.plant_name) return item.plant_name;
      if (item.item_type === 'Snack Boxes' && item.snack_box_items?.length) {
        const names = item.snack_box_items.map(s => `${s.item_name} x${s.quantity}`).join(', ');
        return `Snack Box: ${names}`;
      }
      return item.item_type;
    });
    return descs.join('; ');
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
  };

  const handleColumnFilter = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
  };

  const uniqueAgents = [...new Set((orders || []).map(o => o.agent_name).filter(Boolean))].sort();

  const filteredAndSortedOrders = useMemo(() => {
    let list = [...(Array.isArray(orders) ? orders : [])];

    if (columnFilters.order_number) {
      const q = columnFilters.order_number.toLowerCase().trim();
      list = list.filter(o => (o.order_number || '').toLowerCase().includes(q));
    }
    if (columnFilters.category) {
      list = list.filter(o => o.category === columnFilters.category);
    }
    if (columnFilters.product) {
      const q = columnFilters.product.toLowerCase().trim();
      list = list.filter(o => getProductDescription(o).toLowerCase().includes(q));
    }
    if (columnFilters.customer) {
      const q = columnFilters.customer.toLowerCase().trim();
      list = list.filter(o => (o.customer_name || '').toLowerCase().includes(q));
    }
    if (columnFilters.agent) {
      list = list.filter(o => (o.agent_name || '-') === columnFilters.agent);
    }
    if (columnFilters.settlement) {
      const status = columnFilters.settlement === 'Settled' ? 'Settled' : 'Pending';
      list = list.filter(o => (o.settlement_status || 'Pending') === status);
    }

    const mult = sortAsc ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'order_number') {
        cmp = (a.order_number || '').localeCompare(b.order_number || '', undefined, { numeric: true });
      } else if (sortColumn === 'date') {
        cmp = moment(a.delivery_date).valueOf() - moment(b.delivery_date).valueOf();
      } else if (sortColumn === 'category') {
        cmp = (a.category || '').localeCompare(b.category || '');
      } else if (sortColumn === 'product') {
        cmp = getProductDescription(a).localeCompare(getProductDescription(b));
      } else if (sortColumn === 'customer') {
        cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
      } else if (sortColumn === 'agent') {
        cmp = (a.agent_name || '-').localeCompare(b.agent_name || '-');
      } else if (sortColumn === 'total') {
        cmp = parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0);
      } else if (sortColumn === 'paid') {
        cmp = parseFloat(a.paid_amount || 0) - parseFloat(b.paid_amount || 0);
      } else if (sortColumn === 'due') {
        cmp = parseFloat(a.due_amount || 0) - parseFloat(b.due_amount || 0);
      } else if (sortColumn === 'settlement') {
        const sa = (a.settlement_status || 'Pending') === 'Settled' ? 1 : 0;
        const sb = (b.settlement_status || 'Pending') === 'Settled' ? 1 : 0;
        cmp = sa - sb;
      }
      return mult * (cmp || 0);
    });
    return list;
  }, [orders, sortColumn, sortAsc, columnFilters]);

  const exportToExcel = () => {
    const headers = ['Order #', 'Date', 'Category', 'Product Description', 'Customer', 'Agent', 'Total', 'Paid', 'Due', 'Settlement'];
    const rows = filteredAndSortedOrders.map(o => [
      o.order_number,
      formatDateDisplay(o.delivery_date),
      o.category,
      getProductDescription(o),
      o.customer_name,
      o.agent_name || '-',
      parseFloat(o.total_amount || 0).toFixed(2),
      parseFloat(o.paid_amount || 0).toFixed(2),
      parseFloat(o.due_amount || 0).toFixed(2),
      (o.settlement_status || 'Pending') === 'Settled' ? (o.settlement_type || 'Settled') : 'Pending'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accountant-orders-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="accountant-view">
      <PageNavbar />
      <div className="container">
        <h1>Accountant View</h1>

        <div className="accountant-quick-links" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <Link to="/expenses" className="btn btn-primary">
            Review POS expenses
          </Link>
          <Link to="/pos-inventory" className="btn btn-secondary">
            Store inventory
          </Link>
        </div>

        <div className="order-search-section card">
          <h2>Quick open order</h2>
          <OrderSearch placeholder="Enter order ID (e.g. CX-1234 or 1234)" />
        </div>

        {summary && (
          <div className="summary card">
            <h2>Summary</h2>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Total Orders</span>
                <span className="summary-value">{summary.totalOrders}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Revenue</span>
                <span className="summary-value">₹{summary.totalRevenue.toFixed(2)}</span>
              </div>
              <div className="summary-item summary-outstanding">
                <span className="summary-label">Outstanding</span>
                <span className="summary-value">₹{summary.totalOutstanding.toFixed(2)}</span>
              </div>
              <div className="summary-item summary-settled">
                <span className="summary-label">Settled ({summary.settledCount})</span>
                <span className="summary-value">₹{summary.totalSettled.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="filters card">
          <h2>Filters</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Settlement Status</label>
              <select
                className="form-control"
                value={settlementFilter}
                onChange={(e) => setSettlementFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Settled">Settled</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading orders...</div>
        ) : error ? (
          <div className="card error-card">
            <p className="error-message">{error}</p>
            <button type="button" className="btn btn-primary" onClick={() => fetchOrders()}>
              Retry
            </button>
          </div>
        ) : (
          <div className="orders-table card">
            <div className="orders-table-header">
              <h2>Orders</h2>
              <button type="button" className="btn btn-primary btn-sm" onClick={exportToExcel}>
                Export to Excel
              </button>
            </div>
            <table className="table accountant-orders-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('order_number')} title="Sort">
                      Order # {sortColumn === 'order_number' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <input
                      type="text"
                      className="th-filter-input"
                      placeholder="Filter..."
                      value={columnFilters.order_number}
                      onChange={(e) => handleColumnFilter('order_number', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('date')} title="Sort">
                      Date {sortColumn === 'date' && (sortAsc ? '↑' : '↓')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('category')} title="Sort">
                      Category {sortColumn === 'category' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <select
                      className="th-filter-select"
                      value={columnFilters.category}
                      onChange={(e) => handleColumnFilter('category', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">All</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('product')} title="Sort">
                      Products {sortColumn === 'product' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <input
                      type="text"
                      className="th-filter-input"
                      placeholder="Filter..."
                      value={columnFilters.product}
                      onChange={(e) => handleColumnFilter('product', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('customer')} title="Sort">
                      Customer {sortColumn === 'customer' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <input
                      type="text"
                      className="th-filter-input"
                      placeholder="Filter..."
                      value={columnFilters.customer}
                      onChange={(e) => handleColumnFilter('customer', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('agent')} title="Sort">
                      Agent {sortColumn === 'agent' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <select
                      className="th-filter-select"
                      value={columnFilters.agent}
                      onChange={(e) => handleColumnFilter('agent', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">All</option>
                      {uniqueAgents.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('total')} title="Sort">
                      Total {sortColumn === 'total' && (sortAsc ? '↑' : '↓')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('paid')} title="Sort">
                      Paid {sortColumn === 'paid' && (sortAsc ? '↑' : '↓')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('due')} title="Sort">
                      Due {sortColumn === 'due' && (sortAsc ? '↑' : '↓')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort-btn" onClick={() => handleSort('settlement')} title="Sort">
                      Settlement {sortColumn === 'settlement' && (sortAsc ? '↑' : '↓')}
                    </button>
                    <select
                      className="th-filter-select"
                      value={columnFilters.settlement}
                      onChange={(e) => handleColumnFilter('settlement', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Settled">Settled</option>
                    </select>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedOrders.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="no-orders-cell">
                      No orders found for the selected date range and filters.
                    </td>
                  </tr>
                ) : filteredAndSortedOrders.map(order => {
                  const isSettled = (order.settlement_status || 'Pending') === 'Settled';
                  const isEventCat = ['Events', 'N Caterers', 'Caterers'].includes(order.category);
                  return (
                    <tr
                      key={order.id}
                      className={`order-row-clickable ${isSettled ? 'row-settled' : 'row-pending'}`}
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <td><strong>{order.order_number}</strong></td>
                      <td>{formatDateDisplay(order.delivery_date)}</td>
                      <td>{order.category}</td>
                      <td className="product-desc-cell" title={getProductDescription(order)}>{getProductDescription(order)}</td>
                      <td>{order.customer_name}</td>
                      <td>{order.agent_name || '-'}</td>
                      <td>₹{parseFloat(order.total_amount || 0).toFixed(2)}</td>
                      <td>₹{parseFloat(order.paid_amount || 0).toFixed(2)}</td>
                      <td className={order.due_amount > 0 ? 'due-amount' : ''}>₹{parseFloat(order.due_amount || 0).toFixed(2)}</td>
                      <td>
                        {isEventCat ? (
                          <span className="badge" title="Settled via Caterer SOA">Caterer SOA</span>
                        ) : (
                          <span className={`badge settlement-${(order.settlement_status || 'Pending').toLowerCase()}`}>
                            {isSettled ? `${order.settlement_type || 'Settled'}` : 'Pending'}
                          </span>
                        )}
                      </td>
                      <td className="actions-cell" onClick={e => e.stopPropagation()}>
                        {isEventCat ? (
                          order.caterer_id ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/caterers/${order.caterer_id}/account`);
                              }}
                            >
                              Caterer SOA
                            </button>
                          ) : (
                            <span className="muted">—</span>
                          )
                        ) : !isSettled && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              onClick={(e) => openAddPayment(order, e)}
                            >
                              Add Payment
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={(e) => openSettle(order, e)}
                            >
                              Settle
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Payment Modal */}
      {addPaymentModal && (
        <div className="modal-overlay" onClick={closeAddPayment}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add Payment - {addPaymentModal.order_number}</h3>
            <p className="modal-due">Due: ₹{parseFloat(addPaymentModal.due_amount || 0).toFixed(2)}</p>
            <form onSubmit={handleAddPayment}>
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  className="form-control"
                  value={addPaymentForm.amount}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, amount: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mode</label>
                <select
                  className="form-control"
                  value={addPaymentForm.payment_mode}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_mode: e.target.value })}
                >
                  {PAYMENT_MODES.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={addPaymentForm.payment_date}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  className="form-control"
                  value={addPaymentForm.notes}
                  onChange={(e) => setAddPaymentForm({ ...addPaymentForm, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeAddPayment}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addPaymentSubmitting}>
                  {addPaymentSubmitting ? 'Saving...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settle Modal */}
      {settleModal && (
        <div className="modal-overlay" onClick={closeSettle}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Settle Order - {settleModal.order_number}</h3>
            <p className="modal-due">Total: ₹{parseFloat(settleModal.total_amount || 0).toFixed(2)} | Due: ₹{parseFloat(settleModal.due_amount || 0).toFixed(2)}</p>
            <form onSubmit={handleSettle}>
              <div className="form-group">
                <label>Settlement Type</label>
                <select
                  className="form-control"
                  value={settleForm.settlement_type}
                  onChange={(e) => setSettleForm({ ...settleForm, settlement_type: e.target.value })}
                >
                  {SETTLEMENT_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              {(settleForm.settlement_type === 'Write-off' || settleForm.settlement_type === 'Credit') && (
                <div className="form-group">
                  <label>Notes (required for Write-off / Credit)</label>
                  <textarea
                    className="form-control"
                    value={settleForm.settlement_notes}
                    onChange={(e) => setSettleForm({ ...settleForm, settlement_notes: e.target.value })}
                    placeholder="e.g. Internal employee - called to credit"
                    rows={3}
                    required={settleForm.settlement_type !== 'Full'}
                  />
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeSettle}>Cancel</button>
                <button type="submit" className="btn btn-success" disabled={settleSubmitting}>
                  {settleSubmitting ? 'Settling...' : 'Mark as Settled'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantView;
